import { ref } from 'vue';
import PostSaleCaseNotesAPI from 'dashboard/api/postSaleCaseNotes';

export function usePostSaleCaseNotes({ t, notify }) {
  const notes = ref([]);
  const loadingNotes = ref(false);
  const savingNote = ref(false);
  const editingNote = ref(null);
  const showNoteForm = ref(false);

  const errorMessage = requestError =>
    requestError?.response?.data?.message ||
    requestError?.response?.data?.error ||
    t('POST_SALE.NOTES.ALERTS.LOAD_ERROR');

  const clear = () => {
    notes.value = [];
    editingNote.value = null;
    showNoteForm.value = false;
  };

  const load = async postSaleCaseId => {
    if (!postSaleCaseId) return;
    loadingNotes.value = true;

    try {
      const { data } = await PostSaleCaseNotesAPI.list(postSaleCaseId);
      notes.value = data.data || [];
    } catch (requestError) {
      notes.value = [];
      notify(errorMessage(requestError));
    } finally {
      loadingNotes.value = false;
    }
  };

  const openCreate = () => {
    editingNote.value = null;
    showNoteForm.value = true;
  };

  const openEdit = note => {
    editingNote.value = note;
    showNoteForm.value = true;
  };

  const closeForm = () => {
    editingNote.value = null;
    showNoteForm.value = false;
  };

  const save = async (postSaleCaseId, payload) => {
    savingNote.value = true;

    try {
      const request = editingNote.value
        ? PostSaleCaseNotesAPI.update(
            postSaleCaseId,
            editingNote.value.id,
            payload
          )
        : PostSaleCaseNotesAPI.create(postSaleCaseId, payload);
      const { data } = await request;
      const savedNote = data.data;
      notes.value = editingNote.value
        ? notes.value.map(note => (note.id === savedNote.id ? savedNote : note))
        : [savedNote, ...notes.value];
      notify(
        editingNote.value
          ? t('POST_SALE.NOTES.ALERTS.UPDATED')
          : t('POST_SALE.NOTES.ALERTS.CREATED')
      );
      closeForm();
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    } finally {
      savingNote.value = false;
    }
  };

  const remove = async (postSaleCaseId, note) => {
    try {
      await PostSaleCaseNotesAPI.destroy(postSaleCaseId, note.id, {
        expected_version: note.version,
      });
      notes.value = notes.value.filter(item => item.id !== note.id);
      notify(t('POST_SALE.NOTES.ALERTS.DELETED'));
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    }
  };

  return {
    clear,
    closeForm,
    editingNote,
    load,
    loadingNotes,
    notes,
    openCreate,
    openEdit,
    remove,
    save,
    savingNote,
    showNoteForm,
  };
}
